import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import { useMediaQuery } from '@mantine/hooks'
import { useState } from 'react'
import Image from 'next/image'
import ReactPlayer from 'react-player'
import Slider, { Settings } from 'react-slick'
import styles from './kdt-ugm-5th.module.scss'

interface IUgmProjectProps {
  projectDetailList: {
    id: number
    step: string
    title: string
    desc?: string
    contents: {
      contentsImagePc: string
      contentsImageM: string
      contentsDesc: string
    }[]
  }[]
}

export const ProjectSliderItem = ({ projectDetailList }: IUgmProjectProps) => {
  const settings = {
    dots: true,
    infinite: false,
    variableWidth: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: false,
    touchMove: true,
    arrows: true,
  }

  return (
    <div>
      <Slider {...settings}>
        {projectDetailList.map(detail => (
          <div className="w-full overflow-hidden pt-[98px] lg:pt-[70px]" key={detail.id}>
            <div className="flex flex-col items-center overflow-hidden rounded-lg bg-trueGray-800 lg:flex-row lg:gap-20 lg:rounded">
              <div className="flex w-full flex-col gap-3 px-4 py-3">
                <h6 className="text-base font-bold text-white">{detail.title}</h6>
                {detail.contents.map(content => (
                  <div className="flex flex-col gap-3" key={content.contentsDesc}>
                    <div className="relative">
                      <img
                        className="relative z-10 h-full w-full object-contain"
                        src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-ugm-5th/section4_gif_deco_m.png"
                        alt="-"
                      />
                      <img
                        src={content.contentsImageM}
                        className="absolute left-0 top-0 h-full w-full object-contain"
                        loading="lazy"
                        alt={content.contentsDesc}
                      />
                    </div>
                    {detail.desc && (
                      <p className="text-sm leading-normal text-white">{detail.desc}</p>
                    )}
                    <p className="text-xs text-label-alternative">{content.contentsDesc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IProjectOutputProps {
  contents: {
    imgUrl: string
    thumbnailM: string
    thumbnailPc: string
    step: string
    title: string
  }[]
}

export const ProjectOutputSliderItem = ({ contents }: IProjectOutputProps) => {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const [activeIndex, setActiveIndex] = useState(0)
  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '31%',
    touchMove: true,
    variableWidth: false,
    afterChange: setActiveIndex, // 현재 슬라이드 인덱스 업데이트
    responsive: [
      {
        breakpoint: 2100,
        settings: {
          dots: false,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '27%',
        },
      },
      {
        breakpoint: 1501,
        settings: {
          dots: false,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '20%',
        },
      },
      {
        breakpoint: 1023,
        settings: {
          dots: true,
          arrows: true,
          infinite: true,
          slidesToShow: 1,
          centerPadding: '24px',
        },
      },
    ],
  }

  const isInViewThumbnail = (idx: number, visibleCount: number = 2) => {
    const totalContents = contents.length
    // 현재 인덱스의 이전 n개, 현재, 다음 n개 인덱스를 계산 (순환 처리)
    const indices = []

    // 현재 인덱스 추가
    indices.push(activeIndex)

    // 이전 n개 인덱스 추가
    for (let i = 1; i <= visibleCount; i++) {
      indices.push((activeIndex - i + totalContents) % totalContents)
    }

    // 다음 n개 인덱스 추가
    for (let i = 1; i <= visibleCount; i++) {
      indices.push((activeIndex + i) % totalContents)
    }

    return indices.includes(idx)
  }

  return (
    <Slider {...settings}>
      {contents.map(({ imgUrl, thumbnailM, thumbnailPc, step, title }, idx) => (
        <div key={title} className="px-1 lg:px-0">
          <div
            className={`${styles.section9_mediaquery} relative flex w-full flex-col items-center justify-center overflow-hidden rounded-lg bg-gray-900`}
          >
            <div
              className="h-auto w-full overflow-hidden bg-black"
              style={{
                position: 'relative',
                paddingBottom: '56%',
              }}
            >
              {/* 썸네일 표시, 화면에 보이는 썸네일만 렌더링 */}
              {isInViewThumbnail(idx) && (
                <img
                  src={isMobile ? thumbnailM : thumbnailPc}
                  alt={title}
                  className="absolute left-0 top-0 z-10 h-full w-full object-cover transition-opacity duration-300"
                  style={{
                    opacity: activeIndex === idx ? 0 : 1,
                  }}
                />
              )}

              {activeIndex === idx && (
                <>
                  {/* 비디오 (slick-active일 때만 재생) */}
                  <ReactPlayer
                    url={imgUrl}
                    controls={false}
                    playing={activeIndex === idx} // ✅ index로 비교
                    width="100%"
                    height="100%"
                    muted={true} // ✅ iOS에서 자동재생을 허용하려면 muted가 반드시 true여야 함
                    playsInLine
                    playsinline={true}
                    loop
                    config={{
                      file: {
                        attributes: {
                          autoPlay: true,
                          muted: true,
                          playsInline: true,
                          controls: false,
                        },
                      },
                    }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </>
              )}
            </div>
            <div className="flex w-full flex-row items-center justify-start gap-2 px-4 py-3 lg:gap-4 lg:px-6 lg:py-8">
              <p className="flex h-6 w-6 items-center justify-center rounded-sm border border-yellow-450 bg-[rgba(255,119,16,0.1)] text-base font-bold text-yellow-450 lg:h-[28px] lg:w-[28px] lg:text-lg">
                {step}
              </p>
              <p className="text-base font-bold text-white lg:text-xl">{title}</p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface ISection8Props {
  contents: {
    imgUrlM: string
    imgUrlPc: string
    step: string
    title: string
    desc: string
  }[]
}

export const Section8SliderItem = ({ contents }: ISection8Props) => {
  const settings: Settings = {
    dots: true,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    centerMode: true,
    centerPadding: '24px',
    touchMove: true,
    variableWidth: false,
  }

  return (
    <Slider {...settings}>
      {contents.map(({ imgUrlM, step, title, desc }) => (
        <div key={title} className="px-1">
          <div className="h-[380px] w-full overflow-hidden rounded-lg">
            <div className="h-auto w-full object-contain">
              <Image
                src={imgUrlM}
                layout="fill"
                objectFit="cover"
                className="!relative w-full"
                alt="-"
              />
            </div>
            <div className="flex min-h-[208px] flex-1 flex-col rounded-b-lg border border-[#D4D4D4] bg-white p-4">
              <p className="mb-3 max-w-fit rounded bg-[rgba(255,119,16,0.1)] px-2 py-1 text-xs font-bold text-yellow-450">
                {step}
              </p>
              <h6 className="mb-3 whitespace-pre-line text-lg font-bold leading-normal text-label-normal">
                {title}
              </h6>
              <p className="flex-1 text-base leading-normal text-label-normal">{desc}</p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}

interface IBenefitCarouselProps {
  contents: {
    imgUrlM: string
    title: string
    desc: string
  }[]
}

export const BenefitSliderItem = ({ contents }: IBenefitCarouselProps) => {
  const settings: Settings = {
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    dots: true,
    arrows: true,
    variableWidth: true,
    touchMove: true,
    className: 'carousel',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '20px',
        },
      },
    ],
  }
  return (
    <div className={`${styles.benefit_slider}`}>
      <Slider {...settings}>
        {contents.map(({ desc, title, imgUrlM }) => (
          <div className="w-full" key={title}>
            <div className="pl-4 lg:pl-8">
              <div>
                <img
                  src={imgUrlM}
                  className="mx-auto mb-[18px] block h-full w-full rounded-sm object-cover"
                  loading="lazy"
                />
                <p className="mb-2 text-base font-semibold text-white">{title}</p>
                <p className="text-base leading-normal text-white lg:whitespace-pre-wrap">{desc}</p>
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}

interface IReviewProps {
  contents: {
    imgUrl: string
    review: string
    highlights: string[]
  }[]
}

export const ReviewSliderItem = ({ contents }: IReviewProps) => {
  const HighlightedText = ({ text, highlights }: { text: string; highlights: string[] }) => {
    const parts = text.split(new RegExp(`(${highlights.join('|')})`, 'g'))
    return (
      <p className="text-base font-bold leading-normal text-white">
        {parts.map(part =>
          highlights.includes(part) ? (
            <span key={`highlight-${part}`} className="text-yellow-450">
              {part}
            </span>
          ) : (
            <span key={`text-${part}`}>{part}</span>
          ),
        )}
      </p>
    )
  }

  const settings: Settings = {
    dots: false,
    arrows: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    speed: 500,
    touchMove: true,
    variableWidth: true,
    centerMode: false,
    centerPadding: '0',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          infinite: true,
          slidesToShow: 1,
          dots: true,
          arrows: false,
          centerMode: false,
          centerPadding: '0',
          variableWidth: true,
        },
      },
    ],
  }
  return (
    <Slider {...settings}>
      {contents.map(({ imgUrl, review, highlights }) => (
        <div key={imgUrl} className="w-full">
          <div className="h-[280px] pl-4">
            <div className="flex h-full w-full flex-col gap-4 rounded-lg bg-trueGray-800 p-4">
              <div className="flex flex-row items-start gap-4 border-b border-[#404040] pb-4">
                <div className="h-12 w-12 shrink-0 overflow-hidden">
                  <Image
                    src={imgUrl}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt="-"
                  />
                </div>
              </div>
              <p className="flex-1">
                <HighlightedText text={review} highlights={highlights} />
              </p>
            </div>
          </div>
        </div>
      ))}
    </Slider>
  )
}
